import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AllLabels } from '../../../../constants/app.constants';
import { KeyValueData } from '../../../../domains/common.domain';

import { CommonService } from '../../../../services/common.service';
import { AllDropdownKeys } from '../../../jurisdiction.constants';
import { LookupOptionsData } from '../../../jurisdiction.domain';
import { FacilitiesFormService } from '../../facilities-form.service';
import { EnvironmentService } from '../../../../services/environment.service';
import { FacilitiesHiddenDropdownData } from 'src/app/interface/config.interface';

@Component({
  selector: 'app-special-appliance',
  styleUrls: ['./special-appliance.component.scss'],
  templateUrl: './special-appliance.component.html',
})
export class SpecialApplianceComponent implements OnInit {
  @Input() isReadOnly: boolean = false;
  @Input() currentIndex: number = 0;
  @Input() parentIndex: number = 0;
  @Input() specialApplianceData: any = null;
  @Input() displayDeleteButton: boolean = false;

  @Input() set hideAdditionalData(value: boolean) {
    this.showAdditionalData = !value;
    this.updateAdditionalDataControls();
  }

  @Output() deleteComponent: EventEmitter<number>;
  @Output() specialApplianceSelection: EventEmitter<string>;

  blockIntervalsKey: string;
  blockIntervals: LookupOptionsData[];
  isSpecialApplianceSectionRequired: boolean;
  specialApplianceKey: string;
  specialAppliances: LookupOptionsData[];
  specialApplianceType: string;
  specialApplianceFG: FormGroup;
  allUnits: KeyValueData;
  showAdditionalData: boolean = true;
  showSubjectToApprovalCheckbox: boolean;
  hiddenDropdownData: FacilitiesHiddenDropdownData;

  get subjectToApprovalRequirementsMet(): boolean {
    const requirementsMet =
      this.specialApplianceType === 'wallbox' &&
      this.specialApplianceFG.controls['power'].value &&
      Number(this.specialApplianceFG.controls['power'].value) >= 11;

    if (requirementsMet) {
      this.specialApplianceFG.addControl('deviceRequiresApproval', new FormControl(this.specialApplianceData?.manufacturer || ''));
    } else {
      this.specialApplianceFG.removeControl('deviceRequiresApproval');
    }

    return requirementsMet;
  }

  constructor(
    private commonService: CommonService,
    public environmentService: EnvironmentService,
    private facilitiesFormService: FacilitiesFormService
  ) {
    this.deleteComponent = new EventEmitter<number>();
    this.specialApplianceSelection = new EventEmitter<string>();
    this.allUnits = AllLabels.units;
  }

  ngOnInit(): void {
    this.hiddenDropdownData = this.environmentService.facilityHiddenDropdownData;
    this.specialApplianceKey = AllDropdownKeys.SpecialConsumerType;
    this.specialAppliances = this.commonService.getDropdownData(this.specialApplianceKey);
    this.modifyDropdownCollections();
    this.blockIntervalsKey = AllDropdownKeys.SWKNBlockIntervalsType;
    this.blockIntervals = this.commonService.getDropdownData(this.blockIntervalsKey);
    this.isSpecialApplianceSectionRequired = this.environmentService.isSpecialApplianceSectionRequired;
    this.showSubjectToApprovalCheckbox = this.environmentService.showSubjectToApprovalCheckbox;
    this.prepareForm();
    this.facilitiesFormService.manageSpecialApplianceFormData({
      component: 'specialAppliance',
      formData: this.specialApplianceFG,
      index: this.currentIndex,
      parentIndex: this.parentIndex,
    });

    this.facilitiesFormService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.resetData();
      }
    });
  }

  prepareForm(): void {
    const selectedAppliance =
      (this.specialApplianceData?.specialAppliance &&
        this.specialAppliances.filter(item => item.value === this.specialApplianceData.specialAppliance)[0]) ||
      null;
    this.specialApplianceType = selectedAppliance?.value || '';

    const validatorsList = this.isSpecialApplianceSectionRequired ? [Validators.required] : [];
    this.specialApplianceFG = new FormGroup({
      deviceId: new FormControl(this.specialApplianceData?.deviceId || ''),
      facilityId: new FormControl(this.specialApplianceData?.facilityId || ''),
      specialAppliance: new FormControl(this.specialApplianceType, validatorsList),
      power: new FormControl(this.specialApplianceData?.power || '', validatorsList),
      is_public_facility: new FormControl(
        { value: this.specialApplianceData?.is_public_facility, disabled: this.isReadOnly },
        validatorsList
      ),
    });

    this.specialApplianceFG.controls.power.valueChanges.subscribe(() => {
      this.showModuleType();
    });
    if (this.environmentService.showBlockIntervalsField) {
      this.specialApplianceFG.addControl('blockIntervals', new FormControl(''));
      if (this.specialApplianceType === 'heatpump') {
        this.specialApplianceFG.controls.blockIntervals.setValue(this.specialApplianceData?.blockIntervals || '');
        this.specialApplianceFG.controls.blockIntervals.updateValueAndValidity();
      }
    }

    if (this.showAdditionalData) {
      this.updateAdditionalDataControls();
    }

    if (this.specialApplianceType) {
      this.selectSpecialAppliance();
    }
  }

  selectSpecialAppliance(event: any = null): void {
    if (event) {
      this.specialApplianceType = event.value;
    }
    if (this.environmentService.showBlockIntervalsField && this.specialApplianceType !== 'heatpump') {
      this.specialApplianceFG.controls.blockIntervals.reset();
    }

    if (this.specialApplianceType === 'wallbox' || this.specialApplianceType === 'charger') {
      this.specialApplianceFG.addControl(
        'consumptionDeviceCheckbox',
        new FormControl((this.specialApplianceData && this.specialApplianceData.consumptionDeviceCheckbox) || false)
      );
      this.specialApplianceFG.addControl(
        'publicDeviceCheckbox',
        new FormControl((this.specialApplianceData && this.specialApplianceData.publicDeviceCheckbox) || false)
      );
    } else if (this.specialApplianceType === 'other') {
      this.specialApplianceFG.addControl('other_appliance', new FormControl('', [Validators.required]));
      this.specialApplianceFG.controls.other_appliance.markAsTouched();
      this.specialApplianceFG.controls.other_appliance.updateValueAndValidity();
    } else {
      this.specialApplianceFG.removeControl('consumptionDeviceCheckbox');
      this.specialApplianceFG.removeControl('publicDeviceCheckbox');
      this.specialApplianceFG.removeControl('other_appliance');
    }
    this.showModuleType();
    this.specialApplianceSelection.emit(this.specialApplianceType);
  }

  removeElement(): void {
    this.deleteComponent.emit(this.currentIndex);
  }

  private showModuleType(): void {
    if (
      (this.specialApplianceType === 'heatpump' || this.specialApplianceType === 'wallbox' || this.specialApplianceType === 'charger') &&
      this.specialApplianceFG?.controls?.power?.value > 4.2
    ) {
      const validatorsList = this.isSpecialApplianceSectionRequired ? [Validators.required] : [];
      this.specialApplianceFG.addControl(
        'module_type',
        new FormControl({ value: this.specialApplianceData?.module_type, disabled: this.isReadOnly }, validatorsList)
      );
      this.specialApplianceFG.controls.module_type.updateValueAndValidity();
    } else {
      this.specialApplianceFG.removeControl('module_type');
    }
  }

  private modifyDropdownCollections(): void {
    if (this.hiddenDropdownData?.generationFacility) {
      this.specialAppliances = this.specialAppliances.filter(sa => sa.value !== 'generator');
    }
  }

  private resetData(): void {
    this.specialApplianceType = '';
    this.currentIndex = 0;
  }

  private updateAdditionalDataControls() {
    if (!this.specialApplianceFG) {
      return;
    }

    if (this.showAdditionalData) {
      const validatorsList = this.isSpecialApplianceSectionRequired ? [Validators.required] : [];

      this.specialApplianceFG.addControl('amount', new FormControl(this.specialApplianceData?.amount || '', validatorsList));
      if (!this.environmentService.copyExistingTechnicalDataConfiguration) {
        this.specialApplianceFG.addControl('typeName', new FormControl(this.specialApplianceData?.typeName || '', validatorsList));
        this.specialApplianceFG.addControl('manufacturer', new FormControl(this.specialApplianceData?.manufacturer || '', validatorsList));
      }
    } else {
      this.specialApplianceFG.removeControl('typeName');
      this.specialApplianceFG.removeControl('manufacturer');
      this.specialApplianceFG.removeControl('amount');
    }

    this.specialApplianceFG.updateValueAndValidity();
  }
}
